import { HOSTED_BASE_URL } from '@onefootprint/dev-tools';
import { getErrorMessage } from '@onefootprint/fetch-dashboard';
import type { PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Dialog, TextInput, useToast } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useOrgCreateOnboardingSession from './use-org-create-onboarding-session';
import UserSpecificForm, { type UserSpecificFormData, type UserSpecificFormHandle } from './user-specific-form';
export type CopyLinkHandler = {
  launch: () => void;
};
export type CopyLinkProps = {
  playbook: PlaybookVersionNoGraph;
};
const CopyLink = forwardRef<CopyLinkHandler, CopyLinkProps>(({
  playbook
}, ref) => {
  const {
    t
  } = useTranslation('playbooks', {
    keyPrefix: 'table.actions.copyLinkDialog'
  });
  const [open, setOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [permanentLink, setPermanentLink] = useState('');
  const createOnboardingSession = useOrgCreateOnboardingSession();
  const [step, setStep] = useState<'config' | 'link'>('config');
  const {
    toast
  } = useToast();
  const formRef = useRef<UserSpecificFormHandle>(null);
  useImperativeHandle(ref, () => ({
    launch: () => setOpen(true)
  }), []);
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(permanentLink);
    setShowSuccess(true);
    scheduleToHideConfirmation();
  };
  const scheduleToHideConfirmation = () => {
    setTimeout(() => {
      setShowSuccess(false);
    }, 600);
  };
  const handleClose = () => {
    setOpen(false);
    setStep('config');
    setShowSuccess(false);
  };
  const handleFormSubmit = async (formData: UserSpecificFormData) => {
    try {
      const response = await createOnboardingSession.mutateAsync({
        body: {
          key: playbook.key,
          useImplicitAuth: false,
          ttlMin: 24 * 60 * 7,
          bootstrapData: {
            ...(formData.email ? {
              'id.email': formData.email
            } : {}),
            ...(formData.phone ? {
              'id.phone_number': formData.phone
            } : {})
          },
          ...(formData.external_id ? {
            userExternalId: formData.external_id
          } : {})
        }
      });

      // Backend returns a host based on its own SERVICE_ENVIRONMENT, so a local
      // dashboard hitting a dev/preview API would get a non-localhost link that
      // doesn't open here. Rewrite to the dashboard's hosted origin.
      const link = new URL(response.link);
      const base = new URL(HOSTED_BASE_URL);
      link.protocol = base.protocol;
      link.host = base.host;
      setPermanentLink(link.toString());
      setStep('link');
    } catch (error) {
      const message = getErrorMessage(error);
      if (message.includes('user_external_id')) {
        formRef.current?.setExternalIdError(message);
        return;
      }
      toast({
        title: t($ => $.config.errors.failed),
        description: message,
        variant: 'error'
      });
    }
  };
  const handleBack = () => {
    setStep('config');
  };
  const getDialogProps = () => {
    if (step === 'config') {
      return {
        title: t($_0 => $_0.config.title),
        primaryButton: {
          label: t($_1 => $_1.config.actions.continue),
          type: 'submit' as const,
          form: 'user-specific-form',
          loading: createOnboardingSession.isPending
        },
        secondaryButton: {
          label: t($_2 => $_2.config.actions.cancel),
          onClick: handleClose,
          type: 'button' as const
        }
      };
    }
    return {
      title: t($_3 => $_3.link.title),
      primaryButton: {
        label: showSuccess ? t($_4 => $_4.link.actions.copied) : t($_5 => $_5.link.actions.copyLink),
        onClick: handleCopyToClipboard,
        type: 'button' as const
      },
      secondaryButton: {
        label: t($_6 => $_6.link.actions.back),
        onClick: handleBack,
        type: 'button' as const
      }
    };
  };
  return <Dialog onClose={handleClose} open={open} size="compact" {...getDialogProps()}>
      <div className="flex flex-col gap-5">
        {step === 'config' && <>
            <div className="flex flex-col gap-1">
              <p className="text-label-2 text-label-2">{t($_7 => $_7[step].title)}</p>
              <p className="text-body-2 text-tertiary">{t($_8 => $_8.config.description)}</p>
            </div>
            <UserSpecificForm ref={formRef} onSubmit={handleFormSubmit} requiresAuthentication={playbook.requiresAuthentication} />
          </>}
        {step === 'link' && <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col gap-1">
              <p className="text-label-2 text-label-2">{t($_9 => $_9[step].title)}</p>
              <p className="text-body-2 text-tertiary">{t($_10 => $_10.link.description)}</p>
            </div>
            <div className="flex-grow-1">
              <TextInput value={permanentLink} aria-label={t($_11 => $_11.link.label)} size="compact" readOnly />
            </div>
          </div>}
      </div>
    </Dialog>;
});
export default CopyLink;